<template>
  <nav
    class="pwa-control-bar d-print-none d-flex align-start justify-center"
    data-testid="pwa-control-bar"
  >
    <!-- TODO: Replace the fallback PWA control aria labels with localized content keys. -->
    <div class="pwa-control-bar__actions d-flex align-center justify-center justify-lg-start flex-grow-1 pl-lg-4">
      <button
        type="button"
        class="pwa-control-bar__button d-inline-flex align-center justify-center pa-0 ma-0 rounded"
        :aria-label="$t('input.btn.back')"
        :title="$t('input.btn.back')"
        data-testid="pwa-control-back"
        @click="goBack"
      >
        <VIcon aria-hidden="true" color="greyedOut" size="32">
          {{ icons.mdiArrowLeft }}
        </VIcon>
      </button>
      <button
        type="button"
        class="pwa-control-bar__button d-inline-flex align-center justify-center pa-0 ma-0 rounded"
        :aria-label="$t('label.home')"
        :title="$t('label.home')"
        data-testid="pwa-control-home"
        @click="goHome"
      >
        <VIcon aria-hidden="true" color="greyedOut" size="32">
          {{ icons.mdiHomeOutline }}
        </VIcon>
      </button>
      <button
        type="button"
        class="pwa-control-bar__button d-inline-flex align-center justify-center pa-0 ma-0 rounded"
        aria-label="Refresh"
        title="Refresh"
        data-testid="pwa-control-refresh"
        @click="refresh"
      >
        <VIcon aria-hidden="true" color="greyedOut" size="32">
          {{ icons.mdiRefresh }}
        </VIcon>
      </button>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import { mdiArrowLeft, mdiHomeOutline, mdiRefresh } from '@mdi/js'
import { PORTAL_ROOT } from '~/utils/portalRoutes'
import { canNavigateBack, refreshCurrentPage } from '~/utils/pwa'

const icons = { mdiArrowLeft, mdiHomeOutline, mdiRefresh }
const router = useRouter()
const { isDemo } = useSAuthState()
const { $isStartRoute } = useNuxtApp()

const goBack = () => {
  if (canNavigateBack($isStartRoute)) {
    router.go(-1)
  }
}

const goHome = () => {
  if (isDemo.value) {
    return navigateTo(PORTAL_ROOT, { external: true })
  }

  return router.push({ name: 'index' })
}

const refresh = () => {
  refreshCurrentPage()
}
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use '~/assets/scss/lib/vars';
@use 'sass:map';

.pwa-control-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  // Keep standalone navigation above dialogs while leaving global blockers on top.
  z-index: vars.$pwa-control-bar-z-index;
  box-sizing: border-box;
  // Both vars are defined on body.pwa-controls (BaseLayout) and inherit down; --pwa-safe-bottom
  // carries the trimmed home-indicator clearance and resets to 0 on desktop, so the top-pinned
  // desktop bar needs no separate height rule.
  height: calc(var(--pwa-control-bar-height, 64px) + var(--pwa-safe-bottom, 0px));
  padding-bottom: var(--pwa-safe-bottom, 0px);
  border-top: 1px solid #e6e6e6;
  background: #fff;

  &__actions {
    height: calc(var(--pwa-control-bar-height, 64px) - 1px);
    gap: 64px;
  }

  &__button {
    width: 48px;
    height: 48px;
    border: 0;
    background: transparent;
    cursor: pointer;

    &:hover {
      background: rgb(var(--v-theme-backgroundDiv));
    }

    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: 2px;
    }
  }

  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    top: 0;
    bottom: auto;
    border-top: 0;
    border-bottom: 1px solid #e6e6e6;

    &__actions {
      gap: 24px;
    }

    &__button {
      width: 42px;
      height: 42px;
    }
  }
}
</style>
